import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { map, of } from 'rxjs';
import { ScopeGuardData } from '../models/scope-guard-data.model';
import { LdapTokenService } from '../services/ldap-token/ldap-token.service';

/**
 * Example usage:
 *
 *  [
 *    {
 *      ...
 *    },
 *    {
 *      loadChildren: () => import('../features/aircraft/aircraft.routes'),
 *      path: 'aircraft',
 *      canActivate: [scopeGuard],
 *      data: {
 *        scopes: [Scope.AIRCRAFT_ADMIN_READ]
 *      }
 *    },
 *    {
 *      ...
 *    }
 *  ]
 */
export const scopeGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const ldapTokenService = inject(LdapTokenService);

  const singularScope = (route.data as ScopeGuardData).scope;
  const scopeArray = (route.data as ScopeGuardData).scopes;
  const requireAll = (route.data as ScopeGuardData).requireAll ?? true;

  if ((singularScope == null && scopeArray == null) || (singularScope != null && scopeArray != null)) {
    return of(false);
  }

  const scopeList = singularScope != null ? [singularScope] : scopeArray!;
  return requireAll
    ? ldapTokenService.hasAllScopes(scopeList).pipe(
        map((allScopesActive: boolean) => {
          if (!allScopesActive && route.data.redirectTo != null && Array.isArray(route.data.redirectTo)) {
            return router.createUrlTree(route.data.redirectTo);
          }
          return allScopesActive;
        })
      )
    : ldapTokenService.hasScopes(scopeList).pipe(map((hasScopes) => hasScopes.some((scope) => scope)));
};
